/* eslint-disable no-restricted-globals */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { addNotification } from 'reapop';
import { FormattedMessage } from 'react-intl';
import { FlatButton, MenuItem, RaisedButton, SelectField, TextField } from 'material-ui';
import { size, map, mapValues, get, isEmpty, orderBy, last, floor } from 'lodash';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import pallet from '../../../constants/colors';
import holidayResponseStatuses from '../../../constants/holidayResponseStatuses';
import { changePeriod, sendNotifyEmail } from '../actions';
import useClockCardEntries from '../useClockCardEntries';
import { custom } from '../../../utils/Api';
import { post } from '../api';
import { fetch as fetchHoliday } from '../../../actions/EmployeeHoliday';

const { OK, WARNING, ERROR } = holidayResponseStatuses;

const errorStyle = {
  color: 'red',
  fontSize: '12px',
  margintop: 20,
};

const inputStyle = { width: 310, textAlign: 'left', marginTop: 10 };
const timeInputRowStyle = {
  display: 'flex', alignItems: 'center', gap: 8, marginTop: 4,
};
const floatingStyle = { textAlign: 'left', width: 340 };

const dateInputStyle = {
  border: '1px solid #ccc',
  padding: '4px 8px',
  textAlign: 'center',
  cursor: 'pointer',
  width: 90,
  height: 20,
  color: '#00000099',
};

const tsWrapStyle = { position: 'relative', display: 'inline-block' };
const tsTriggerStyle = {
  cursor: 'pointer',
  border: '1px solid #ccc',
  padding: '4px 8px',
  width: 44,
  textAlign: 'center',
  fontSize: 14,
  height: 30,
  color: '#00000099',
  boxSizing: 'border-box',
};
const tsListStyle = {
  position: 'absolute',
  zIndex: 1000,
  background: 'white',
  border: '1px solid #ccc',
  maxHeight: 200,
  overflowY: 'auto',
  minWidth: 54,
  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
};
const tsOptionStyle = selected => ({
  padding: '4px 8px',
  cursor: 'pointer',
  textAlign: 'center',
  background: selected ? '#1976d2' : 'white',
  color: selected ? 'white' : 'inherit',
});

const TimeSelect = ({ value, count, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const toggle = () => setOpen(o => !o);

  const handleInput = (e) => {
    const parsed = parseInt(e.target.value, 10);
    if (!isNaN(parsed) && parsed >= 0 && parsed < count) onChange(parsed);
  };

  return (
    <div ref={ref} style={tsWrapStyle}>
      <input
        type="text"
        value={String(value).padStart(2, '0')}
        onChange={handleInput}
        onClick={toggle}
        onKeyDown={(e) => { if (e.key === 'Tab') setOpen(false); }}
        style={tsTriggerStyle}
      />
      {open && (
        <div style={tsListStyle}>
          {Array.from({ length: count }, (_, i) => (
            <div
              key={i}
              role="option"
              aria-selected={i === value}
              tabIndex={-1}
              onClick={() => { onChange(i); setOpen(false); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { onChange(i); setOpen(false); } }}
              style={tsOptionStyle(i === value)}
            >
              {String(i).padStart(2, '0')}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

TimeSelect.propTypes = {
  value: PropTypes.number.isRequired,
  count: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

const DayEntryForm = ({
  defaultValues, selectedEntries, clearAndClose, hrsPerDay,
  entryTypes, primaryButton, setPrimaryButton, whichButton, valuesByKey,
  getRemainingHours, entriesByDate, isEdit,
  dispatch, start, end, timeEmailTypes,
}) => {
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [inOutTimeError, setInOutTimeError] = useState(false);
  const [sameTimeError, setSameTimeError] = useState(false);
  const [moreThan24HoursError, setMoreThan24HoursError] = useState(false);
  const [moreThanZeroError, setMoreThanZeroError] = useState(false);

  const [warningText, setWarningText] = useState('');
  const [warningType, setWarningType] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);

  const DefaultEmployeeDetailID = localStorage.getItem('DefaultEmployeeDetailID');
  const { createClockCardEntries, updateClockCardEntries } = useClockCardEntries();

  const locale = localStorage.language === 'is' ? 'is' : 'en';
  const showInOutInput = get(valuesByKey, ['Time.Use.RegisterClock', 'Value']);

  useEffect(() => {
    setForm(defaultValues);
  }, [defaultValues]);

  const closeModal = () => {
    setIsConfirming(false);
    clearAndClose();
  };

  const validateAndSetErrors = () => {
    const hours = parseFloat(get(form, 'hours'));
    if (hours <= 0 && showInOutInput === 'false') {
      setMoreThanZeroError(true);
      return true;
    }
    setErrors(mapValues(form, v => !v));
    if (!size(get(form, 'date')) || !size(get(form, 'timeIn')) || get(form, 'clockCardEntryTypeID') === null) return true;
    if (inOutTimeError) {
      return true;
    }

    const timeFrom = moment(get(form, 'timeIn'));
    const timeTo = moment(get(form, 'timeOut'));
    const duration = moment.duration(timeTo.diff(timeFrom));
    if (get(form, 'timeOut') && duration.asMinutes() === 0) {
      setSameTimeError(true);
      return true;
    }
    if (duration.asHours() > 24) {
      setMoreThan24HoursError(true);
      return true;
    }

    return false;
  };

  const reset = () => {
    setWarningText('');
    setWarningType('');
    setInOutTimeError(false);
    setSameTimeError(false);
    setMoreThan24HoursError(false);
    setMoreThanZeroError(false);
    setIsConfirming(false);
  };

  const formChanged = (key, value) => {
    reset();
    let current;
    if (key === 'date') {
      const hoursRegistered = hrsPerDay - getRemainingHours(value);
      const remaining = hrsPerDay - hoursRegistered;
      let clockIn = moment(value).set({
        hour: 8, minute: 0, second: 0, millisecond: 0,
      });
      if (hoursRegistered > 0) {
        const employeeEntries = get(entriesByDate, moment(value).format('DD/MM/YY'));
        const lastEntry = get(last(orderBy(employeeEntries, 'ClockOut')), 'ClockOut');
        clockIn = moment(lastEntry);
      }
      const clockOut = moment(clockIn).add(remaining, 'hours');
      current = {
        ...form,
        timeIn: clockIn,
        timeOut: clockOut,
        hours: remaining.toFixed(2),
        date: value,
      };
    } else if (key === 'hours') {
      const hoursRegistered = hrsPerDay - getRemainingHours(get(form, 'date'));

      let clockIn = moment(get(form, 'date')).set({
        hour: 8, minute: 0, second: 0, millisecond: 0,
      });
      if (hoursRegistered > 0 && !isEdit) {
        const employeeEntries = get(entriesByDate, moment(get(form, 'date')).format('DD/MM/YY'));
        const lastEntry = get(last(orderBy(employeeEntries, 'ClockOut')), 'ClockOut');
        clockIn = moment(lastEntry);
      }
      const clockOut = moment(clockIn).add(value, 'hours');


      current = {
        ...form, timeIn: clockIn, timeOut: clockOut, hours: value
      };
    } else {
      current = { ...form, [key]: value };
    }
    setForm(current);
    setErrors({ ...errors, [key]: !value });
  };

  const onPressDay = (day) => {
    reset();
    setPrimaryButton(day);
    if (hrsPerDay > 0.00) {
      const clockIn = moment(get(form, 'date')).set({
        hour: 8, minute: 0, second: 0, millisecond: 0,
      });
      const clockOut = moment(clockIn);
      let hrs = hrsPerDay;
      if (day === 'halfDay') {
        clockOut.add((hrsPerDay / 2).toFixed(2), 'hours');
        hrs = (hrsPerDay / 2).toFixed(2);
      } else {
        clockOut.add(hrsPerDay, 'hours');
      }
      const current = {
        ...form, timeIn: clockIn.format('YYYY-MM-DD HH:mm'), timeOut: clockOut.format('YYYY-MM-DD HH:mm'), hours: hrs
      };
      setForm(current);
    }
  };

  const getDuration = (timeIn, timeOut) => {
    if (timeIn !== null && timeOut !== null) {
      const timeFrom = moment(timeIn);
      const timeTo = moment(timeOut);
      const duration = moment.duration(timeTo.diff(timeFrom));
      const mins = duration.asMinutes();
      let hours = floor(mins / 60);
      let minutes = floor(mins % 60);
      if (duration.seconds() >= 30) {
        minutes += 1;
      }
      const hoursInDigits = duration.asHours().toFixed(2);
      const isClockOutBeforeIn = timeTo.isBefore(timeFrom);

      whichButton(hoursInDigits);

      if (isClockOutBeforeIn) {
        setInOutTimeError(true);
        return '';
      }
      if (isNaN(hours)) { hours = 0; }
      if (isNaN(minutes)) { minutes = 0; }

      return `${hours}h ${minutes}m`;
    }
    return `${0}h ${0}m`;
  };

  const submit = async () => {
    try {
      if (!validateAndSetErrors()) {
        setLoading(true);
        const clockIn = moment(get(form, 'timeIn'));
        const clockOut = moment(get(form, 'timeOut'));
        let hours = parseFloat(get(form, 'hours'));
        if (showInOutInput === 'true') {
          const duration = moment.duration(clockOut.diff(clockIn));
          hours = parseFloat(duration.asHours());
        }
        let status;
        let messages = [];
        const Comment = get(form, 'clockCardEntryComment') || '';
        const ClockCardEntryTypeID = get(form, 'clockCardEntryTypeID');
        const entryType = get(entryTypes, ClockCardEntryTypeID);
        const isVacation = get(entryType, 'ClockCardEntryTypeRule') === 'Vacation';

        const isCreating = isEmpty(selectedEntries);

        if (isCreating) {
          // Check if user provided a clockOut value
          const hasClockOut = get(form, 'timeOut') && get(form, 'timeOut') !== '';

          if (!hasClockOut) {
            // User didn't fill in ClockOut - create open entry using old post method
            if (isVacation) {
              const msg = locale === 'is' ? 'Ekki er hægt að skrá opna orlofs færslu. Vinsamlegast fylltu út bæði "Inn" og "Út" reitina.'
                : 'Vacation is being registered without an out time. Please fill in both "In" and "Out" fields.';
              setWarningText(msg);
              setWarningType('ERROR');
              setLoading(false);
              return;
            }
            const openEntryData = {
              ClockCardEntryComment: Comment,
              ClockCardEntryTypeID,
              ClockIn: clockIn.toISOString(),
              ClockOut: null,
              EmployeeDetailID: parseInt(DefaultEmployeeDetailID, 10)
            };
            await custom(post(openEntryData));
            status = OK;
            messages = [];
          } else {
            // User filled in ClockOut - check if it's on the next day
            let finalClockOut = clockOut.toISOString();
            if (!clockIn.isSame(clockOut, 'day')) {
              // ClockOut is on the next day, then we need to set PeriodEndda to null
              finalClockOut = null;
            }

            const data = {
              ClockCardEntryTypeID,
              Comment,
              EmployeeDetailID: parseInt(DefaultEmployeeDetailID, 10),
              PeriodBegda: clockIn.toISOString(),
              PeriodEndda: finalClockOut,
              HoursPerDay: hours,
              IncludesWeekends: false,
              IncludesRedDays: false,
              UseAccrualsIfNeeded: isConfirming,
            };
            const res = await createClockCardEntries(data);
            status = get(res, 'status');
            messages = get(res, 'messages', []);
          }
        } else {
          const data = {
            ClockCardEntryTypeID,
            ClockCardEntryComment: Comment,
            ClockCardEntryIDs: map(selectedEntries, 'ClockCardEntryID'),
            ClockIn: clockIn.toISOString(),
            ClockOut: clockOut.toISOString(),
            UseAccrualsIfNeeded: isConfirming,
          };
          const res = await updateClockCardEntries(data);
          status = get(res, 'status');
          messages = get(res, 'messages', []);
        }
        if (status === OK) {
          setLoading(false);
          closeModal();
          dispatch(changePeriod(start, end));

          // Refresh holiday data if vacation entry because user could be located on dashboard.
          if (isVacation) {
            dispatch(fetchHoliday());
          }

          dispatch(addNotification({
            title: <FormattedMessage id={isCreating ? 'time.notification.post.title' : 'time.notification.patch.title'} />,
            message: messages.join('. '),
            status: 'success',
            dismissible: true,
            dismissAfter: 5000,
          }));
          // Send notification email if creating new entry
          if (isCreating && clockOut && size(timeEmailTypes) > 0) {
            const entry = {
              ClockCardEntryTypeID: get(form, 'clockCardEntryTypeID'),
            };
            await sendNotifyEmail(DefaultEmployeeDetailID, clockIn.toISOString(), clockOut.toISOString(), entryTypes, timeEmailTypes, entry);
          }
        } else if (status === WARNING || status === ERROR) {
          setWarningText(messages.join('. '));
          setWarningType(status);
          setIsConfirming(status === WARNING);
          setLoading(false);
        }
      }
    } catch (error) {
      dispatch(addNotification({
        title: <FormattedMessage id="time.error" />,
        message: get(error, 'message'),
        status: 'error',
        dismissible: true,
        dismissAfter: 3000,
      }));
    }
  };

  return (
    <div>
      <div style={{ marginTop: 16, display: 'flex', }}>
        <RaisedButton
          label={<FormattedMessage id="time.wholeDay" />}
          primary={primaryButton === 'wholeDay'}
          onClick={() => onPressDay('wholeDay')}
        />
        <RaisedButton
          label={<FormattedMessage id="time.halfDay" />}
          primary={primaryButton === 'halfDay'}
          onClick={() => onPressDay('halfDay')}
        />
      </div>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
      }}
      >
        {showInOutInput === 'true' ? (
          <div>
            <div>
              <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.3)', marginTop: 14 }}>
                <FormattedMessage id="time.ClockIn" />
              </div>
              <div style={timeInputRowStyle}>
                <DatePicker
                  selected={get(form, 'timeIn', null) ? moment(form.timeIn) : null}
                  onChange={(date) => {
                    const prev = get(form, 'timeIn') ? moment(form.timeIn) : moment().set({ hour: 8, minute: 0 });
                    formChanged('timeIn', moment(date).set({ hour: prev.hours(), minute: prev.minutes() }));
                  }}
                  dateFormat="DD.MM.YYYY"
                  placeholderText="DD.MM.YYYY"
                  customInput={<input style={dateInputStyle} />}
                />
                <TimeSelect
                  value={get(form, 'timeIn') ? moment(form.timeIn).hours() : 8}
                  count={24}
                  onChange={h => formChanged('timeIn', moment(get(form, 'timeIn') || undefined).set({ hour: h }))}
                />
                <span>:</span>
                <TimeSelect
                  value={get(form, 'timeIn') ? moment(form.timeIn).minutes() : 0}
                  count={60}
                  onChange={m => formChanged('timeIn', moment(get(form, 'timeIn') || undefined).set({ minute: m }))}
                />
              </div>
              {errors.timeIn && <div style={errorStyle}><FormattedMessage id="editPanel.required" /></div>}
            </div>
            <div>
              <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.3)', marginTop: 14 }}>
                <FormattedMessage id="time.ClockOut" />
              </div>
              <div style={timeInputRowStyle}>
                <DatePicker
                  selected={get(form, 'timeOut', null) ? moment(form.timeOut) : null}
                  onChange={(date) => {
                    const prev = get(form, 'timeOut') ? moment(form.timeOut) : moment().set({ hour: 17, minute: 0 });
                    formChanged('timeOut', moment(date).set({ hour: prev.hours(), minute: prev.minutes() }));
                  }}
                  dateFormat="DD.MM.YYYY"
                  placeholderText="DD.MM.YYYY"
                  customInput={<input style={dateInputStyle} />}
                />
                <TimeSelect
                  value={get(form, 'timeOut') ? moment(form.timeOut).hours() : 17}
                  count={24}
                  onChange={h => formChanged('timeOut', moment(get(form, 'timeOut') || undefined).set({ hour: h }))}
                />
                <span>:</span>
                <TimeSelect
                  value={get(form, 'timeOut') ? moment(form.timeOut).minutes() : 0}
                  count={60}
                  onChange={m => formChanged('timeOut', moment(get(form, 'timeOut') || undefined).set({ minute: m }))}
                />
              </div>
            </div>

            {inOutTimeError && <div style={errorStyle}><FormattedMessage id="time.error.TimeOutBeforeTimeIn" /></div>}
            {sameTimeError && <div style={errorStyle}><FormattedMessage id="time.error.inAndOutSame" /></div>}
          </div>
        ) : (
          <div>
            <TextField
              id="date"
              type="date"
              floatingLabelFixed
              floatingLabelText={<FormattedMessage id="time.Date" />}
              errorText={errors.date ? <FormattedMessage id="editPanel.required" /> : ''}
              value={get(form, 'date')}
              onChange={(event) => { formChanged('date', event.target.value); }}
            />
            <TextField
              type="number"
              value={get(form, 'hours', 0)}
              onChange={(_, val) => { formChanged('hours', val); }}
              hintStyle={inputStyle}
              style={inputStyle}
              inputStyle={inputStyle}
              floatingLabelStyle={floatingStyle}
              floatingLabelFixed
              errorText={errors.hours ? <FormattedMessage id="editPanel.required" /> : ''}
              floatingLabelText={<FormattedMessage id="time.NumberOfHours" />}
            />
          </div>
        )}
        {moreThan24HoursError ? <div style={errorStyle}><FormattedMessage id="time.error.MoreThan24HoursError" /></div> : ''}
        {moreThanZeroError ? <div style={errorStyle}><FormattedMessage id="time.error.MoreThanZero" /></div> : ''}
        {(get(form, 'timeOut') !== '' && get(form, 'timeIn') !== '' && inOutTimeError !== true && sameTimeError !== true) &&
          <div style={{ marginTop: 10 }}>
            {getDuration(get(form, 'timeIn'), get(form, 'timeOut'))}
          </div>}
      </div>


      <SelectField
        fullWidth
        floatingLabelFixed
        floatingLabelText={<FormattedMessage id="time.Type" />}
        value={get(form, 'clockCardEntryTypeID')}
        onChange={(event, index, value) => { formChanged('clockCardEntryTypeID', value); }}
      >
        {map(entryTypes, ({ ClockCardEntryTypeID, ClockCardEntryTypeName, ClockCardEntryTypeName_EN: NameEnglish }) => (
          <MenuItem key={ClockCardEntryTypeID} value={ClockCardEntryTypeID} primaryText={locale === 'is' ? ClockCardEntryTypeName : (NameEnglish || ClockCardEntryTypeName)} />
        ))}
      </SelectField>
      {errors.clockCardEntryTypeID && <div style={errorStyle}><FormattedMessage id="editPanel.required" /></div>}
      <span style={{ color: warningType === 'ERROR' ? 'red' : pallet.warningColor }}>{warningText && warningText}</span>
      <TextField
        fullWidth
        floatingLabelFixed
        floatingLabelText={<FormattedMessage id="time.Comment" />}
        multiLine
        value={get(form, 'clockCardEntryComment')}
        onChange={(_, comment) => formChanged('clockCardEntryComment', comment)}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
        <FlatButton
          label={<FormattedMessage id="time.cancel" />}
          secondary
          onClick={() => closeModal()}
          disabled={loading}
        />
        {!isConfirming ? (
          <RaisedButton
            type="submit"
            label={size(selectedEntries) ? <FormattedMessage id="time.update" /> : <FormattedMessage id="time.create" />}
            primary
            onClick={submit}
            disabled={loading || inOutTimeError || sameTimeError || get(errors, 'date', false) === true || get(errors, 'timeIn', false) === true || size(get(form, 'clockCardEntryTypeID')) > 0}
            loading={`${loading}`}
          />
        ) : (
          <RaisedButton
            type="submit"
            label={<FormattedMessage id="alert.ok" />}
            primary
            onClick={submit}
            disabled={loading || inOutTimeError || sameTimeError || get(errors, 'date', false) === true || get(errors, 'timeIn', false) === true || size(get(form, 'clockCardEntryTypeID')) > 0}
            loading={`${loading}`}
          />
        )}
      </div>
    </div>
  );
};


DayEntryForm.propTypes = {
  clearAndClose: PropTypes.func.isRequired,
  defaultValues: PropTypes.shape({
    clockCardEntryComment: PropTypes.string,
    clockCardEntryTypeID: PropTypes.number,
    timeIn: PropTypes.string,
    timeOut: PropTypes.string,
  }).isRequired,
  entryTypes: PropTypes.shape({}).isRequired,
  hrsPerDay: PropTypes.string.isRequired,
  primaryButton: PropTypes.string.isRequired,
  selectedEntries: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setPrimaryButton: PropTypes.func.isRequired,
  whichButton: PropTypes.func.isRequired,
  valuesByKey: PropTypes.object,
  entriesByDate: PropTypes.shape({}).isRequired,
  getRemainingHours: PropTypes.func.isRequired,
  isEdit: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired,
  start: PropTypes.object.isRequired,
  end: PropTypes.object.isRequired,
  timeEmailTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default DayEntryForm;



// WEBPACK FOOTER //
// ./src/sites/time/components/DayEntryForm.js